import { useMemo, useRef, useState } from "react";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { clsx, fmtDate } from "./Primitives";

/**
 * Web: how www.saga-advisory.com and info.saga-advisory.com are doing, from Google Analytics
 * and Search Console — tied to the CRM where it matters (markets with deals, searches that
 * name our accounts, leads, which newsletter brought visitors).
 */

export type Site = "www" | "info";
export const SITE_LABEL: Record<Site, string> = { www: "www.saga-advisory.com", info: "info.saga-advisory.com" };
export const SITE_COLOR: Record<Site, string> = { www: "#1F7FA8", info: "#b0873b" };   // validated pair (CVD ΔE ≥ 17)

export interface WebStatus { analytics: boolean; search_console: boolean; missing: string[]; service_account?: string | null; property_id?: string | null; gsc_site?: Record<Site, string> }
interface SiteStats { users: number; sessions: number; views: number; prev_users: number; engagement: number; users_change: number | null; views_change: number | null }
interface Overview {
  days: number; fetched_at: string; sites: Record<Site, SiteStats>;
  daily: { date: string; www: number; info: number }[];
  channels: { channel: string; www: number; info: number }[];
  newsletter_campaigns: { campaign: string; sessions: number; users: number; newsletter: { id: string; name: string; subject: string; sent_at: string | null } | null }[];
  leads: { date: string; n: number }[];
}
interface Pages { pages: { path: string; title: string; url: string; views: number; users: number; seconds: number; change: number | null; new: boolean }[]; fetched_at: string }
interface Query { query: string; clicks: number; impressions: number; ctr: number; position: number; prev_clicks?: number; match?: { kind: string; id: string; name: string } }
interface Search {
  totals: { clicks: number; impressions: number; ctr: number; position: number }; previous: { clicks: number; impressions: number; ctr: number; position: number };
  queries: Query[]; mentions: Query[]; pages: { url: string; clicks: number; impressions: number; ctr: number; position: number }[]; period: { startDate: string; endDate: string }; fetched_at: string;
}
interface Countries { countries: { country: string; www: number; info: number; users: number; change: number | null; market: { slug: string; flag: string | null; deals: number } | null }[]; fetched_at: string }

export const n0 = (n: number) => n.toLocaleString("en-GB");
export function Change({ v, invert }: { v: number | null | undefined; invert?: boolean }) {
  if (v === null || v === undefined) return <span className="wb-chg flat">new</span>;
  const good = invert ? v < 0 : v > 0;
  return <span className={clsx("wb-chg", v === 0 ? "flat" : good ? "up" : "down")}>{v > 0 ? "▲" : v < 0 ? "▼" : "•"} {Math.abs(v)}%</span>;
}
const since = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

export default function Web({ go, isAdmin }: { go: (s: string, id?: string | null) => void; isAdmin: boolean }) {
  const { data: st } = useData<WebStatus>("/api/web/status");
  const [tab, setTab] = useState<"overview" | "pages" | "search" | "countries">("overview");
  const [days, setDays] = useState(28);
  const [site, setSite] = useState<Site>("info");
  const [v, setV] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = async () => { setBusy(true); try { await api.post("/api/web/refresh", {}); setV(x => x + 1); } finally { setBusy(false); } };

  return (
    <div className="screen web-screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Web</h1>
          <div className="page-sub">www and info.saga-advisory.com · Google Analytics and Search Console</div>
        </div>
        {st?.analytics && (
          <div className="wb-controls">
            <div className="seg sm">
              {[7, 28, 90].map(d => <button key={d} className={clsx("seg-btn", days === d && "active")} onClick={() => setDays(d)}>{d} days</button>)}
            </div>
            {isAdmin && <button className="btn ghost sm" disabled={busy} onClick={refresh} title="Fetch new numbers from Google now">{busy ? "Refreshing…" : "Refresh"}</button>}
          </div>
        )}
      </div>

      {!st ? <div className="card wb-pad">Loading…</div> : !st.analytics ? <Setup st={st} isAdmin={isAdmin} /> : (
        <>
          <div className="wb-tabs">
            {([["overview", "Overview"], ["pages", "Pages"], ["search", "Google search"], ["countries", "Countries"]] as const).map(([k, l]) => (
              <button key={k} className={clsx("wb-tab", tab === k && "on")} onClick={() => setTab(k)}>{l}</button>
            ))}
            {(tab === "pages" || tab === "search") && (
              <div className="seg sm wb-site">
                {(["info", "www"] as Site[]).map(s => <button key={s} className={clsx("seg-btn", site === s && "active")} onClick={() => setSite(s)}>
                  <i className="wb-key" style={{ background: SITE_COLOR[s] }} />{s === "www" ? "www" : "info"}</button>)}
              </div>
            )}
          </div>
          {tab === "overview" && <OverviewTab days={days} v={v} go={go} />}
          {tab === "pages" && <PagesTab days={days} site={site} v={v} />}
          {tab === "search" && <SearchTab days={days} site={site} v={v} go={go} />}
          {tab === "countries" && <CountriesTab days={days} v={v} go={go} />}
        </>
      )}
    </div>
  );
}

/* ---- Setup (until the Google connection is in place) ---- */

function Setup({ st, isAdmin }: { st: WebStatus; isAdmin: boolean }) {
  if (!isAdmin) return <div className="card wb-pad">Website statistics aren't connected yet. An admin can set them up here.</div>;
  return (
    <div className="card wb-pad wb-setup">
      <h3>Connect Google Analytics and Search Console</h3>
      <p>Both sites send to <b>G-NJT2VL7WDD</b>. That ID only <i>sends</i> data — to <i>read</i> it, the CRM needs a Google service account with read access.</p>
      <ol>
        <li><b>GA4 property ID</b> (a number): <a href="https://analytics.google.com/" target="_blank" rel="noreferrer">analytics.google.com</a> → Admin → Property details. Put it in the server's <code>.env</code> as <code>GA4_PROPERTY_ID</code>.{st.property_id && <span className="wb-ok"> ✓ set ({st.property_id})</span>}</li>
        <li><b>Service account</b>: <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noreferrer">Google Cloud → Service accounts</a> → Create → Keys → Add key → JSON. Put the file's contents in <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> (a secret — never in chat or email).{st.service_account && <span className="wb-ok"> ✓ {st.service_account}</span>}</li>
        <li><b>Turn on the APIs</b> in the same project: <a href="https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com" target="_blank" rel="noreferrer">Analytics Data API</a> and <a href="https://console.cloud.google.com/apis/library/searchconsole.googleapis.com" target="_blank" rel="noreferrer">Search Console API</a>.</li>
        <li><b>Give it read access</b>: Analytics → Admin → Property access management → add the service account as <b>Viewer</b>; and <a href="https://search.google.com/search-console/users" target="_blank" rel="noreferrer">Search Console → Users</a> → add it as <b>Restricted</b>.</li>
        <li><b>Search Console property</b>: the domain property <code>sc-domain:saga-advisory.com</code> is used by default. For separate URL-prefix properties set <code>GSC_SITE_WWW</code> and <code>GSC_SITE_INFO</code>.</li>
      </ol>
      <p className="wb-muted">Still missing: {st.missing.join(", ") || "nothing"} — restart the CRM after changing <code>.env</code>.</p>
    </div>
  );
}

function useWeb<T>(path: string, v: number) {
  return useData<T>(path, [v]);
}
function Loading({ error }: { error: string | null }) {
  return <div className={clsx("card wb-pad", error && "wb-err")}>{error || "Fetching from Google…"}</div>;
}

/* ---- Overview ---- */

function OverviewTab({ days, v, go }: { days: number; v: number; go: (s: string, id?: string | null) => void }) {
  const { data: o, error } = useWeb<Overview>(`/api/web/overview?days=${days}`, v);
  if (!o) return <Loading error={error} />;
  const leadTotal = o.leads.reduce((a, l) => a + l.n, 0);
  const maxCh = Math.max(1, ...o.channels.map(c => c.www + c.info));
  return (
    <>
      <div className="wb-tiles">
        {(["www", "info"] as Site[]).map(s => {
          const x = o.sites[s];
          return (
            <div key={s} className="card wb-tile">
              <div className="wb-tile-h"><i className="wb-key" style={{ background: SITE_COLOR[s] }} />{SITE_LABEL[s]}</div>
              <div className="wb-tile-row">
                <div><b className="mono">{n0(x.users)}</b><span>visitors <Change v={x.users_change} /></span></div>
                <div><b className="mono">{n0(x.views)}</b><span>page views <Change v={x.views_change} /></span></div>
                <div><b className="mono">{x.engagement}%</b><span>engaged</span></div>
              </div>
            </div>
          );
        })}
        <div className="card wb-tile">
          <div className="wb-tile-h">Leads in the CRM</div>
          <div className="wb-tile-row">
            <div><b className="mono">{leadTotal}</b><span>new leads</span></div>
            <div><b className="mono">{o.sites.www.users + o.sites.info.users ? (Math.round(leadTotal / (o.sites.www.users + o.sites.info.users) * 1000) / 10) : 0}%</b><span>of visitors</span></div>
          </div>
          <button className="ld-link" onClick={() => go("leads")}>Open Leads ›</button>
        </div>
      </div>

      <div className="card wb-card">
        <div className="wb-card-h"><h3>Visitors per day</h3><span className="wb-muted">Updated {since(o.fetched_at)} · last {days} days</span></div>
        <LineChart rows={o.daily} />
      </div>

      <div className="wb-cols">
        <div className="card wb-card">
          <div className="wb-card-h"><h3>Where visits come from</h3></div>
          <div className="wb-bars">
            {o.channels.slice(0, 8).map(c => (
              <div key={c.channel} className="wb-bar-row" title={`${c.channel}: www ${n0(c.www)} · info ${n0(c.info)} sessions`}>
                <span className="wb-bar-l">{c.channel}</span>
                <span className="wb-bar-track">
                  {c.www > 0 && <i style={{ width: `${(c.www / maxCh) * 100}%`, background: SITE_COLOR.www }} />}
                  {c.info > 0 && <i style={{ width: `${(c.info / maxCh) * 100}%`, background: SITE_COLOR.info }} />}
                </span>
                <span className="wb-bar-v mono">{n0(c.www + c.info)}</span>
              </div>
            ))}
            {!o.channels.length && <div className="wb-muted">No visits yet.</div>}
          </div>
          <Legend />
        </div>
        <div className="card wb-card">
          <div className="wb-card-h"><h3>Visits from our newsletters</h3><span className="wb-muted">last {Math.max(days, 90)} days</span></div>
          {o.newsletter_campaigns.length ? (
            <table className="wb-table">
              <thead><tr><th>Newsletter</th><th className="r">Visits</th><th className="r">Readers</th></tr></thead>
              <tbody>
                {o.newsletter_campaigns.map(c => (
                  <tr key={c.campaign}>
                    <td>{c.newsletter ? <button className="wb-a" onClick={() => go("newsletters", c.newsletter!.id)}>{c.newsletter.name}</button> : c.campaign}
                      {c.newsletter?.sent_at && <span className="wb-muted"> · {fmtDate(c.newsletter.sent_at.slice(0, 10))}</span>}</td>
                    <td className="r mono">{n0(c.sessions)}</td><td className="r mono">{n0(c.users)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="wb-muted">None yet. Links in newsletters sent from now on are tagged, so their visits show up here.</p>}
        </div>
      </div>
    </>
  );
}

function Legend() {
  return <div className="wb-legend">{(["www", "info"] as Site[]).map(s => <span key={s}><i className="wb-key" style={{ background: SITE_COLOR[s] }} />{SITE_LABEL[s]}</span>)}</div>;
}

/** Two lines (www, info) on one axis, crosshair tooltip, direct end labels, and a table view. */
function LineChart({ rows }: { rows: { date: string; www: number; info: number }[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const ref = useRef<SVGSVGElement>(null);
  const W = 900, H = 240, L = 44, R = 110, T = 14, B = 26;
  const max = useMemo(() => {
    const m = Math.max(1, ...rows.flatMap(r => [r.www, r.info]));
    const step = Math.pow(10, Math.floor(Math.log10(m)));
    return Math.ceil(m / step) * step;
  }, [rows]);
  if (!rows.length) return <p className="wb-muted">No visits in this period.</p>;
  const x = (i: number) => L + (rows.length === 1 ? 0 : (i / (rows.length - 1)) * (W - L - R));
  const y = (val: number) => T + (1 - val / max) * (H - T - B);
  const path = (s: Site) => rows.map((r, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(r[s]).toFixed(1)}`).join("");
  const ticks = [0, max / 2, max];
  const labelEvery = Math.ceil(rows.length / 7);
  const onMove = (e: React.PointerEvent) => {
    const box = ref.current!.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * W;
    const i = Math.round(((px - L) / (W - L - R)) * (rows.length - 1));
    setHover(Math.max(0, Math.min(rows.length - 1, i)));
  };
  const last = rows[rows.length - 1];
  const h = hover !== null ? rows[hover] : null;
  // Keep the two end labels from colliding.
  let ly = { www: y(last.www), info: y(last.info) };
  if (Math.abs(ly.www - ly.info) < 14) { const mid = (ly.www + ly.info) / 2; ly = last.www >= last.info ? { www: mid - 8, info: mid + 8 } : { www: mid + 8, info: mid - 8 }; }

  return (
    <div className="wb-chart">
      {table ? (
        <div className="wb-table-scroll">
          <table className="wb-table">
            <thead><tr><th>Date</th><th className="r">www</th><th className="r">info</th></tr></thead>
            <tbody>{rows.map(r => <tr key={r.date}><td>{fmtDate(r.date)}</td><td className="r mono">{n0(r.www)}</td><td className="r mono">{n0(r.info)}</td></tr>)}</tbody>
          </table>
        </div>
      ) : (
        <div className="wb-svg-wrap">
          <svg ref={ref} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Visitors per day for www and info"
            onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
            {ticks.map(t => (
              <g key={t}>
                <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} className="wb-grid" />
                <text x={L - 8} y={y(t) + 4} className="wb-axis" textAnchor="end">{n0(t)}</text>
              </g>
            ))}
            {rows.map((r, i) => i % labelEvery === 0 && <text key={r.date} x={x(i)} y={H - 6} className="wb-axis" textAnchor="middle">{fmtDate(r.date)}</text>)}
            {(["www", "info"] as Site[]).map(s => <path key={s} d={path(s)} fill="none" stroke={SITE_COLOR[s]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />)}
            {(["www", "info"] as Site[]).map(s => (
              <g key={s}>
                <circle cx={x(rows.length - 1)} cy={y(last[s])} r={4} fill={SITE_COLOR[s]} stroke="#fff" strokeWidth={2} />
                <text x={x(rows.length - 1) + 10} y={ly[s] + 4} className="wb-end">{s} {n0(last[s])}</text>
              </g>
            ))}
            {h && hover !== null && (
              <g>
                <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} className="wb-cross" />
                {(["www", "info"] as Site[]).map(s => <circle key={s} cx={x(hover)} cy={y(h[s])} r={4.5} fill={SITE_COLOR[s]} stroke="#fff" strokeWidth={2} />)}
              </g>
            )}
          </svg>
          {h && hover !== null && (
            <div className="wb-tip" style={{ left: `${(x(hover) / W) * 100}%` }}>
              <div className="wb-tip-h">{fmtDate(h.date, { weekday: "short", day: "numeric", month: "short" })}</div>
              {(["www", "info"] as Site[]).map(s => <div key={s} className="wb-tip-r"><i style={{ background: SITE_COLOR[s] }} /><b className="mono">{n0(h[s])}</b> {s}</div>)}
            </div>
          )}
        </div>
      )}
      <div className="wb-chart-foot"><Legend /><button className="ld-link" onClick={() => setTable(t => !t)}>{table ? "Show chart" : "Show as table"}</button></div>
    </div>
  );
}

/* ---- Pages ---- */

function PagesTab({ days, site, v }: { days: number; site: Site; v: number }) {
  const { data, error } = useWeb<Pages>(`/api/web/pages?site=${site}&days=${days}`, v);
  if (!data) return <Loading error={error} />;
  const max = Math.max(1, ...data.pages.map(p => p.views));
  return (
    <div className="card wb-card">
      <div className="wb-card-h"><h3>Most read on {SITE_LABEL[site]}</h3><span className="wb-muted">Updated {since(data.fetched_at)}</span></div>
      <table className="wb-table">
        <thead><tr><th>Page</th><th className="r">Views</th><th className="r">vs before</th><th className="r">Readers</th><th className="r">Time each</th></tr></thead>
        <tbody>
          {data.pages.map(p => (
            <tr key={p.path}>
              <td className="wb-page">
                <a href={p.url} target="_blank" rel="noreferrer">{p.title || p.path}</a>
                <span className="wb-path">{p.path}</span>
                <span className="wb-inline-bar"><i style={{ width: `${(p.views / max) * 100}%`, background: SITE_COLOR[site] }} /></span>
              </td>
              <td className="r mono">{n0(p.views)}</td>
              <td className="r">{p.new ? <span className="wb-chg new">new</span> : <Change v={p.change} />}</td>
              <td className="r mono">{n0(p.users)}</td>
              <td className="r mono">{p.seconds >= 60 ? `${Math.floor(p.seconds / 60)}m ${p.seconds % 60}s` : `${p.seconds}s`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!data.pages.length && <p className="wb-muted">No page views in this period.</p>}
    </div>
  );
}

/* ---- Google search ---- */

const KIND_SCREEN: Record<string, string> = { account: "clients", principal: "principal", market: "countries" };

function SearchTab({ days, site, v, go }: { days: number; site: Site; v: number; go: (s: string, id?: string | null) => void }) {
  const { data, error } = useWeb<Search>(`/api/web/search?site=${site}&days=${days}`, v);
  if (!data) return <Loading error={error} />;
  const t = data.totals, p = data.previous;
  const ch = (a: number, b: number) => (b ? Math.round(((a - b) / b) * 100) : null);
  return (
    <>
      <div className="wb-tiles four">
        <div className="card wb-tile"><div className="wb-tile-h">Clicks from Google</div><b className="mono big">{n0(t.clicks)}</b><Change v={ch(t.clicks, p.clicks)} /></div>
        <div className="card wb-tile"><div className="wb-tile-h">Times shown</div><b className="mono big">{n0(t.impressions)}</b><Change v={ch(t.impressions, p.impressions)} /></div>
        <div className="card wb-tile"><div className="wb-tile-h">Click rate</div><b className="mono big">{t.ctr}%</b><span className="wb-muted">before {p.ctr}%</span></div>
        <div className="card wb-tile"><div className="wb-tile-h">Average position</div><b className="mono big">{t.position || "—"}</b><span className="wb-muted">before {p.position || "—"} · lower is better</span></div>
      </div>
      <p className="wb-muted wb-period">Search Console runs about three days behind: {fmtDate(data.period.startDate)} – {fmtDate(data.period.endDate)}.</p>

      {data.mentions.length > 0 && (
        <div className="card wb-card wb-mentions">
          <div className="wb-card-h"><h3>Searches that name our accounts, principals or markets</h3></div>
          <table className="wb-table">
            <thead><tr><th>Search</th><th>In the CRM</th><th className="r">Shown</th><th className="r">Clicks</th></tr></thead>
            <tbody>{data.mentions.map(q => (
              <tr key={q.query}><td>{q.query}</td>
                <td><button className="wb-a" onClick={() => go(KIND_SCREEN[q.match!.kind] || "clients", q.match!.kind === "market" ? null : q.match!.id)}>{q.match!.name}</button> <span className="wb-muted">{q.match!.kind}</span></td>
                <td className="r mono">{n0(q.impressions)}</td><td className="r mono">{n0(q.clicks)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <div className="wb-cols">
        <div className="card wb-card">
          <div className="wb-card-h"><h3>What people searched</h3></div>
          <table className="wb-table">
            <thead><tr><th>Search</th><th className="r">Clicks</th><th className="r">Shown</th><th className="r">Position</th></tr></thead>
            <tbody>{data.queries.slice(0, 25).map(q => (
              <tr key={q.query}><td>{q.query}{(q.prev_clicks ?? 0) === 0 && q.clicks > 0 && <span className="wb-chg new">new</span>}</td>
                <td className="r mono">{n0(q.clicks)}</td><td className="r mono">{n0(q.impressions)}</td><td className="r mono">{q.position}</td></tr>
            ))}</tbody>
          </table>
          {!data.queries.length && <p className="wb-muted">No searches recorded in this period.</p>}
        </div>
        <div className="card wb-card">
          <div className="wb-card-h"><h3>Pages Google sends people to</h3></div>
          <table className="wb-table">
            <thead><tr><th>Page</th><th className="r">Clicks</th><th className="r">Shown</th></tr></thead>
            <tbody>{data.pages.map(pg => (
              <tr key={pg.url}><td className="wb-page"><a href={pg.url} target="_blank" rel="noreferrer">{pg.url.replace(/^https?:\/\/[^/]+/, "") || "/"}</a></td>
                <td className="r mono">{n0(pg.clicks)}</td><td className="r mono">{n0(pg.impressions)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ---- Countries ---- */

function CountriesTab({ days, v, go }: { days: number; v: number; go: (s: string, id?: string | null) => void }) {
  const { data, error } = useWeb<Countries>(`/api/web/countries?days=${days}`, v);
  const [marketsOnly, setMarketsOnly] = useState(false);
  if (!data) return <Loading error={error} />;
  const list = data.countries.filter(c => !marketsOnly || c.market);
  const max = Math.max(1, ...list.map(c => c.users));
  return (
    <div className="card wb-card">
      <div className="wb-card-h">
        <h3>Visitors by country</h3>
        <label className="wb-check"><input type="checkbox" checked={marketsOnly} onChange={e => setMarketsOnly(e.target.checked)} /> Saga markets only</label>
      </div>
      <div className="wb-bars">
        {list.map(c => (
          <div key={c.country} className="wb-bar-row" title={`${c.country}: www ${n0(c.www)} · info ${n0(c.info)} visitors`}>
            <span className="wb-bar-l">
              {c.market?.flag ? `${c.market.flag} ` : ""}{c.country}
              {c.market && <button className="wb-mk" onClick={() => go("countries")}>{c.market.deals ? `market · ${c.market.deals} deal${c.market.deals === 1 ? "" : "s"}` : "market"}</button>}
            </span>
            <span className="wb-bar-track">
              {c.www > 0 && <i style={{ width: `${(c.www / max) * 100}%`, background: SITE_COLOR.www }} />}
              {c.info > 0 && <i style={{ width: `${(c.info / max) * 100}%`, background: SITE_COLOR.info }} />}
            </span>
            <span className="wb-bar-v mono">{n0(c.users)}</span>
            <span className="wb-bar-c"><Change v={c.change} /></span>
          </div>
        ))}
        {!list.length && <div className="wb-muted">No visitors from {marketsOnly ? "Saga markets" : "anywhere"} in this period.</div>}
      </div>
      <Legend />
    </div>
  );
}
